import { useEffect, useRef, useState } from "react";
import { Calendar, ClipboardPlus, FileText, Menu, UserCheck } from "lucide-react";
import type { Conversation } from "../../../types/conversation";
import { LoadingButton } from "../../button/loading-button";
import { THEME } from "../../../styles/theme";
import { formatUserPhone } from "../../../utils";

import { Link } from "react-router-dom";
import { useTenantFeatures } from "../../../contexts/tenant-features";
import { useAuth } from "../../../hooks/use-auth";
import {
  AccessControl,
  APP_RESOURCES,
} from "../../../permissions/access-control";

interface ConversationHeaderProps {
  conversation: Conversation;
  supervisionMode?: boolean;
  onAssume?: () => void;
  isAssuming?: boolean;
  onClose?: () => void;
  onReturnToQueue?: () => void;
  isReturningToQueue?: boolean;
  onReturnToAi?: () => void;
  isReturningToAi?: boolean;
  onBlock?: () => void;
  onSchedule?: () => void;
  onCreateProtocol?: () => void;
  onTemplate?: () => void;
  isTemplateLoading?: boolean;
}

interface ActionMenuItem {
  label: string;
  onClick: () => void;
  className?: string;
  disabled?: boolean;
}

function ActionMenu({
  items,
  className,
}: {
  items: ActionMenuItem[];
  className?: string;
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isMenuOpen) return;

    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!menuRef.current || !target) return;
      if (menuRef.current.contains(target)) return;
      setIsMenuOpen(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isMenuOpen]);

  if (items.length === 0) return null;

  return (
    <div className={`relative ${className ?? ""}`} ref={menuRef}>
      <button
        type="button"
        onClick={() => setIsMenuOpen((prev) => !prev)}
        className="btn-icon h-9 w-9 bg-transparent"
        aria-label="Abrir menu de ações"
        aria-expanded={isMenuOpen}
      >
        <Menu size={20} />
      </button>
      {isMenuOpen && (
        <div className="absolute right-0 mt-2 w-52 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-soft">
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              onClick={() => {
                if (item.disabled) return;
                setIsMenuOpen(false);
                item.onClick();
              }}
              disabled={item.disabled}
              className={`w-full px-4 py-2 text-left text-sm text-slate-800 transition hover:bg-slate-50 ${item.disabled ? "cursor-not-allowed opacity-60" : ""} ${item.className ?? ""}`}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ConversationHeader({
  conversation,
  supervisionMode,
  onAssume,
  isAssuming = false,
  onClose,
  onReturnToQueue,
  isReturningToQueue = false,
  onReturnToAi,
  isReturningToAi = false,
  onBlock,
  onSchedule,
  onCreateProtocol,
  onTemplate,
  isTemplateLoading = false,
}: ConversationHeaderProps) {
  const { protocolsEnabled } = useTenantFeatures();
  const { user } = useAuth();
  const inicial = conversation?.user?.userName?.charAt(0).toUpperCase();
  const avatarColor = conversation?.user?.color ?? THEME.colors.avatarFallback;

  const mobileMenuItems: ActionMenuItem[] = [
    onAssume
      ? {
          label: isAssuming ? "Assumindo conversa..." : "Assumir conversa",
          onClick: onAssume,
          disabled: isAssuming,
        }
      : null,
    onSchedule ? { label: "Agendar consulta", onClick: onSchedule } : null,
    protocolsEnabled &&
    AccessControl.canView(APP_RESOURCES.PANEL_PROTOCOLS, user?.role) &&
    onCreateProtocol
      ? { label: "Criar protocolo", onClick: onCreateProtocol }
      : null,
    onTemplate
      ? {
          label: isTemplateLoading ? "Enviando template..." : "Enviar template",
          onClick: onTemplate,
          disabled: isTemplateLoading,
        }
      : null,
    onReturnToQueue
      ? {
          label: isReturningToQueue
            ? "Devolvendo para fila..."
            : "Devolver para fila",
          onClick: onReturnToQueue,
          disabled: isReturningToQueue,
        }
      : null,
    onReturnToAi
      ? {
          label: isReturningToAi ? "Devolvendo para IA..." : "Devolver para IA",
          onClick: onReturnToAi,
          disabled: isReturningToAi,
        }
      : null,
    onBlock ? { label: "Bloquear contato", onClick: onBlock } : null,
    onClose
      ? {
          label: "Finalizar",
          onClick: onClose,
          className: "text-red-600 hover:bg-red-50",
        }
      : null,
  ].filter(Boolean) as ActionMenuItem[];

  const desktopMenuItems: ActionMenuItem[] = [
    onReturnToQueue
      ? {
          label: isReturningToQueue
            ? "Devolvendo para fila..."
            : "Devolver para fila",
          onClick: onReturnToQueue,
          disabled: isReturningToQueue,
        }
      : null,
    onReturnToAi
      ? {
          label: isReturningToAi ? "Devolvendo para IA..." : "Devolver para IA",
          onClick: onReturnToAi,
          disabled: isReturningToAi,
        }
      : null,
    onBlock ? { label: "Bloquear contato", onClick: onBlock } : null,
  ].filter(Boolean) as ActionMenuItem[];

  return (
    <div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-white px-2">
      <div className="flex items-center gap-3 px-4 py-3">
        <div
          className="w-10 h-10 text-slate-800 rounded-full flex items-center justify-center font-bold"
          style={{ backgroundColor: avatarColor }}
        >
          {inicial}
        </div>

        <div>
          <div className="text-slate-800 font-medium">
            {conversation?.user?.userName}
          </div>
          <div className="text-sm text-emerald-500">WhatsApp</div>
          {protocolsEnabled &&
            AccessControl.canView(APP_RESOURCES.PANEL_PROTOCOLS, user?.role) &&
            conversation.id && (
              <Link
                className="text-xs text-blue-700 underline"
                to={`/painel/protocolos?conversationId=${encodeURIComponent((conversation as Conversation & { conversationId?: string }).conversationId || conversation.id)}`}
              >
                Protocolos deste atendimento
              </Link>
            )}
          {(conversation?.user?.userPhone || conversation?.user?.dialCode) && (
            <div className="text-xs text-slate-500">
              {formatUserPhone(conversation.user)}
            </div>
          )}
          {supervisionMode &&
            (conversation?.systemUser?.name ||
              conversation?.status === "AI_ATTENDANCE") && (
              <div className="text-xs text-slate-500">
                Atendente: {conversation.systemUser?.name ?? "IA"}
              </div>
            )}
        </div>
      </div>

      <div className="flex items-center gap-2 px-4 py-3">
        <ActionMenu items={mobileMenuItems} className="md:hidden" />
        <ActionMenu items={desktopMenuItems} className="hidden md:block" />

        {onSchedule && (
          <button
            type="button"
            onClick={onSchedule}
            className="btn btn-secondary hidden md:inline-flex"
          >
            <Calendar size={16} />
            Agendar consulta
          </button>
        )}

        {protocolsEnabled &&
          AccessControl.canView(APP_RESOURCES.PANEL_PROTOCOLS, user?.role) &&
          onCreateProtocol && (
            <button
              type="button"
              onClick={onCreateProtocol}
              className="btn btn-secondary hidden md:inline-flex"
            >
              <ClipboardPlus size={16} />
              Criar protocolo
            </button>
          )}

        {onTemplate && (
          <LoadingButton
            type="button"
            onClick={onTemplate}
            isLoading={isTemplateLoading}
            loadingLabel="Enviando template"
            className="btn btn-secondary hidden md:inline-flex"
          >
            <FileText size={16} />
            Enviar template
          </LoadingButton>
        )}

        {onAssume && (
          <LoadingButton
            type="button"
            onClick={onAssume}
            isLoading={isAssuming}
            loadingLabel="Assumindo conversa"
            className="btn btn-icon-success hidden md:inline-flex h-auto w-auto px-3 py-2"
          >
            <UserCheck size={16} />
            Assumir conversa
          </LoadingButton>
        )}

        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="btn btn-danger hidden cursor-pointer md:inline-flex"
          >
            Finalizar
          </button>
        )}
      </div>
    </div>
  );
}
